import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { Form } from 'antd';
import dayjs from 'dayjs';

import {
  deleteActionPlan,
  getActionPlanDetails,
  getActionPlanOwners,
  getActionPlansMetrics,
  getCommunityActionPlans,
  patchActionPlan,
} from 'apis/ActionPlansAPI';
import { useSidebarMenuLogic } from 'components/layout/hooks';
import { useRootContext } from 'components/layout/RootContext';
import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { ActionPlanDetailsType, ActionPlanListItemType } from 'types/actionPlans';
import { SelectAdvancedFilterOptionType } from 'types/common';
import { useDebounce } from 'utils/hooks/useDebounce';

import { createActionPlanFromForm, saveActionPlanForm } from './actionPlanFormSave';
import {
  ACTION_PLAN_ALERT_TYPE_FILTER_GROUP_TO_IDS,
  ACTION_PLAN_DEFAULT_TAB,
  ACTION_PLAN_SAVE_ERROR_MESSAGE,
  ACTION_PLAN_SOURCE_FILTER_OPTIONS_BY_TAB,
  ACTION_PLAN_TAB_ALL,
  ACTION_PLAN_TAB_QUERY_PARAM,
  ACTION_PLANS_PAGE_SIZE,
  ActionPlanSourceFilterValue,
  ActionPlanTab,
} from './constants';
import { ActionPlanFormValues } from './CreateEditActionPlansModalTypes';
import { buildItemTypeOptions } from './formUtils';
import useActionPlanOwnerOptions from './useActionPlanOwnerOptions';
import { getCurrentUserDisplayName, isFormValidationError, sortActionPlanSteps, toNumberOrUndefined } from './utils';

export const useActionPlansPage = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { showAlerts, showMarketingCampaigns } = useSidebarMenuLogic();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<ActionPlanTab>(
    searchParams.get(ACTION_PLAN_TAB_QUERY_PARAM) === ACTION_PLAN_TAB_ALL
      ? ACTION_PLAN_TAB_ALL
      : ACTION_PLAN_DEFAULT_TAB
  );
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sourceCategories, setSourceCategories] = useState<ActionPlanSourceFilterValue[]>([]);
  const [alertTypeGroups, setAlertTypeGroups] = useState<number[]>([]);
  const [focusAreaFilters, setFocusAreaFilters] = useState<number[]>([]);
  const [ownerFilters, setOwnerFilters] = useState<number[]>([]);
  const [statusFilters, setStatusFilters] = useState<number[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<ActionPlanListItemType | null>(null);
  const [editPlanDetails, setEditPlanDetails] = useState<ActionPlanDetailsType | null>(null);
  const [form] = Form.useForm<ActionPlanFormValues>();

  const communityId = selectedCommunity?.id;
  const currentUserDisplayName = getCurrentUserDisplayName(currentUser);
  const canViewMarketingCampaigns = Boolean(
    showMarketingCampaigns || currentUser?.isSuperuser || isManagedBySuperuser(currentUser)
  );

  const sourceFilter = activeTab === ACTION_PLAN_TAB_ALL ? undefined : activeTab;
  const debouncedSearch = useDebounce(search, 300);
  const alertTypeIds = useMemo(
    () => alertTypeGroups.flatMap((group) => ACTION_PLAN_ALERT_TYPE_FILTER_GROUP_TO_IDS[group] ?? []),
    [alertTypeGroups]
  );

  const changeTab = (tab: ActionPlanTab) => {
    setActiveTab(tab);
    setPage(1);
    const validSourceValues = new Set(
      (ACTION_PLAN_SOURCE_FILTER_OPTIONS_BY_TAB[tab] ?? []).map((option) => option.value)
    );
    setSourceCategories((current) => current.filter((value) => validSourceValues.has(value)));
    if (tab !== ACTION_PLAN_SOURCE.ALERTS) {
      setAlertTypeGroups([]);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleSourceCategoriesChange = (values: ActionPlanSourceFilterValue[]) => {
    setSourceCategories(values);
    setPage(1);
  };

  const handleAlertTypesChange = (values: number[]) => {
    setAlertTypeGroups(values);
    setPage(1);
  };

  const handleFocusAreaFiltersChange = (values: number[]) => {
    setFocusAreaFilters(values);
    setPage(1);
  };

  const handleOwnerFiltersChange = (values: number[]) => {
    setOwnerFilters(values);
    setPage(1);
  };

  const handleStatusFiltersChange = (values: number[]) => {
    setStatusFilters(values);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch('');
    setSourceCategories([]);
    setAlertTypeGroups([]);
    setFocusAreaFilters([]);
    setOwnerFilters([]);
    setStatusFilters([]);
    setPage(1);
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'action-plans',
      communityId,
      activeTab,
      page,
      debouncedSearch,
      sourceCategories,
      alertTypeGroups,
      focusAreaFilters,
      ownerFilters,
      statusFilters,
    ],
    queryFn: () =>
      getCommunityActionPlans(communityId ?? -1, {
        page,
        page_size: ACTION_PLANS_PAGE_SIZE,
        source: sourceFilter,
        search: debouncedSearch.trim() || undefined,
        source_categories: sourceCategories.length ? sourceCategories : undefined,
        alert_type: alertTypeIds.length ? alertTypeIds : undefined,
        focus_area: focusAreaFilters.length ? focusAreaFilters : undefined,
        owner: ownerFilters.length ? ownerFilters : undefined,
        status: statusFilters.length ? statusFilters : undefined,
      }),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const { data: metrics } = useQuery({
    queryKey: ['action-plans-metrics', communityId],
    queryFn: () => getActionPlansMetrics(communityId ?? -1),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  const { data: ownerUsers } = useQuery({
    queryKey: ['action-plans-owners', communityId],
    queryFn: () => getActionPlanOwners(communityId ?? -1),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  const ownerFilterOptions = useMemo<SelectAdvancedFilterOptionType[]>(
    () => (ownerUsers ?? []).map((user) => ({ value: user.id, label: user.full_name || user.email })),
    [ownerUsers]
  );

  const ownerOptions = useActionPlanOwnerOptions(selectedCommunity?.company?.id, editPlanDetails);
  const itemTypeOptions = useMemo(() => buildItemTypeOptions(editPlanDetails), [editPlanDetails]);

  const plans = data?.results ?? [];
  const visiblePlans = useMemo<ActionPlanListItemType[]>(() => {
    if (canViewMarketingCampaigns) {
      return plans;
    }

    return plans.filter((plan) => !plan.has_email_campaign);
  }, [canViewMarketingCampaigns, plans]);

  const invalidatePlans = () => {
    queryClient.invalidateQueries(['action-plans', communityId]);
    queryClient.invalidateQueries(['action-plans-metrics', communityId]);
    queryClient.invalidateQueries(['action-plans-owners', communityId]);
  };

  const { mutateAsync: assignPlanToMe } = useMutation({
    mutationFn: (actionPlanId: number) => {
      if (!currentUser) {
        return Promise.resolve(null);
      }
      return patchActionPlan(actionPlanId, { owner_id: currentUser.user_id });
    },
    onSuccess: invalidatePlans,
  });

  const { mutateAsync: updatePlanStatus } = useMutation({
    mutationFn: ({ actionPlanId, status }: { actionPlanId: number; status: number }) =>
      patchActionPlan(actionPlanId, { status }),
    onSuccess: invalidatePlans,
  });

  const { mutateAsync: removePlan, isLoading: isRemovingPlan } = useMutation({
    mutationFn: ({ actionPlanId, reason }: { actionPlanId: number; reason?: string }) =>
      deleteActionPlan(actionPlanId, reason?.trim() ? { archived_reason: reason.trim() } : undefined),
    onSuccess: invalidatePlans,
  });

  const closeModal = () => {
    setCreateOpen(false);
    setEditPlan(null);
    setEditPlanDetails(null);
    form.resetFields();
  };

  const { mutateAsync: savePlan, isLoading: isSavingPlan } = useMutation({
    mutationFn: async ({
      values,
      existingPlan,
    }: {
      values: ActionPlanFormValues;
      existingPlan?: ActionPlanListItemType | null;
    }) => {
      if (existingPlan) {
        const currentDetails = editPlanDetails ?? (await getActionPlanDetails(existingPlan.id));
        return saveActionPlanForm({
          actionPlanId: existingPlan.id,
          values,
          currentDetails,
        });
      }

      return createActionPlanFromForm({
        communityId: communityId ?? -1,
        values,
      });
    },
    onSuccess: () => {
      invalidatePlans();
      closeModal();
    },
  });

  const openCreateModal = () => {
    setEditPlan(null);
    setEditPlanDetails(null);
    form.resetFields();
    form.setFieldsValue({
      items: [{ action: '' }],
    });
    setCreateOpen(true);
  };

  const openEditModal = async (plan: ActionPlanListItemType) => {
    setEditPlan(plan);
    form.resetFields();
    const details = await getActionPlanDetails(plan.id);
    setEditPlanDetails(details);
    const sortedSteps = sortActionPlanSteps(details.steps);

    form.setFieldsValue({
      name: plan.name,
      description: plan.description,
      focus_area_id: toNumberOrUndefined(plan.focus_area_id),
      owner_id: toNumberOrUndefined(plan.owner?.id),
      due_date: plan.due_date ? dayjs(plan.due_date) : undefined,
      items: sortedSteps.map((step) => ({
        id: toNumberOrUndefined(step.id),
        action: step.action,
        type_id: toNumberOrUndefined(step.type_id),
        owner_id: toNumberOrUndefined(step.owner?.id),
        due_date: step.due_date ? dayjs(step.due_date) : undefined,
      })),
    });
    setCreateOpen(true);
  };

  const submitPlan = async () => {
    if (isSavingPlan) {
      return;
    }

    try {
      const values = await form.validateFields();
      await savePlan({ values, existingPlan: editPlan });
    } catch (error) {
      if (isFormValidationError(error)) {
        return;
      }

      showMessage('error', ACTION_PLAN_SAVE_ERROR_MESSAGE);
    }
  };

  return {
    currentUser,
    selectedCommunity,
    currentUserDisplayName,
    showAlerts,

    activeTab,
    page,
    setPage,
    search,
    sourceCategories,
    alertTypeGroups,
    focusAreaFilters,
    ownerFilters,
    statusFilters,
    changeTab,
    handleSearchChange,
    handleSourceCategoriesChange,
    handleAlertTypesChange,
    handleFocusAreaFiltersChange,
    handleOwnerFiltersChange,
    handleStatusFiltersChange,
    resetFilters,

    data,
    isLoading,
    isError,
    metrics,
    visiblePlans,
    ownerFilterOptions,

    assignPlanToMe,
    updatePlanStatus,
    removePlan,
    isRemovingPlan,

    createOpen,
    isEditing: Boolean(editPlan),
    editPlan,
    form,
    ownerOptions,
    itemTypeOptions,
    isSavingPlan,
    openCreateModal,
    openEditModal,
    closeModal,
    submitPlan,
  };
};

export default useActionPlansPage;
